import {
  ACTIVITY_OPTIONS_WITH_IDS,
  ACTIVITY_STATUSES_WITH_IDS,
} from '@/constants/filterCriteria/engagement';
import { MEMBERSHIP_STATUSES_WITH_IDS } from '@/constants/filterCriteria/membership';
import { ROSTER_TYPES_WITH_IDS } from '@/constants/filterCriteria/roster';
import FormWithFetchAutocompleteByIdTFC from '@components/LoadSearchTemplateDialog/templateFormControls/FetchAutocompleteByIdTFC';
import FormWithFetchAutocompleteByLabelTFC from '@components/LoadSearchTemplateDialog/templateFormControls/FetchAutocompleteByLabelTFC';
import SelectTFC from '@components/LoadSearchTemplateDialog/templateFormControls/SelectTFC';
import SingleAutocompleteByIdTFC from '@components/LoadSearchTemplateDialog/templateFormControls/SingleAutocompleteByIdTFC';
import TextTFC from '@components/LoadSearchTemplateDialog/templateFormControls/TextTFC';
import { FieldMetaData } from '@components/LoadSearchTemplateDialog/templateFormControls/types';

import type {
  MappedTemplateFormControlMapDeps,
  TemplateFormControlMap,
} from '@components/LoadSearchTemplateDialog/mappedTemplateFormControl/types';

export function buildRosterAndEngagementControls(
  deps: MappedTemplateFormControlMapDeps
): Partial<TemplateFormControlMap> {
  const {
    lookupElectionsOnlyWithElectorates,
    initialValue,
    simpleInitialValue,
    matchedInitialValue,
  } = deps;

  return {
    'roster.electionId': ({ onChildChange }) => (
      <SingleAutocompleteByIdTFC
        label={'Find an election'}
        sx={{ width: '100%' }}
        onChange={onChildChange}
        minLength={3}
        lookup={lookupElectionsOnlyWithElectorates}
        initialValue={initialValue?.[0] ?? null}
      />
    ),
    'roster.rosterTypes': ({ onChildChange }) => (
      <FormWithFetchAutocompleteByLabelTFC
        label={'Specify Roster type(s)'}
        onChange={onChildChange}
        preLoadedOptions={Object.values(ROSTER_TYPES_WITH_IDS)}
        initialValue={simpleInitialValue as string[]}
      />
    ),
    'roster.votingPeriod': ({ onChildChange }) => (
      <SelectTFC
        label={'Voting period'}
        onChange={onChildChange as (value: string, fieldMetaData: FieldMetaData) => void}
        isBlankValid={false}
        variant="filled"
        selectMenuItems={[
          { title: '\u00a0', value: '' },
          { value: 'Any', title: 'Any' },
          { value: 'Early voting', title: 'Early voting' },
          { value: 'Polling Day', title: 'Polling Day' },
        ]}
        initialValue={simpleInitialValue as string}
      />
    ),
    'roster.shiftStatus': ({ onChildChange }) => (
      <SelectTFC
        label={'Shift status'}
        onChange={onChildChange as (value: string, fieldMetaData: FieldMetaData) => void}
        isBlankValid={false}
        variant="filled"
        selectMenuItems={[
          { title: '\u00a0', value: '' },
          { value: 'Any', title: 'Any' },
          { value: 'Confirmed', title: 'Confirmed' },
          { value: 'Draft', title: 'Draft' },
        ]}
        initialValue={simpleInitialValue as string}
      />
    ),
    'membership.statusIds': ({ onChildChange }) => (
      <FormWithFetchAutocompleteByIdTFC
        label={'Select a membership status'}
        onChange={onChildChange}
        minLength={0}
        preLoadedOptions={MEMBERSHIP_STATUSES_WITH_IDS}
        initialValue={matchedInitialValue}
      />
    ),
    'engagementHistory.activityIds': ({ onChildChange }) => (
      <FormWithFetchAutocompleteByIdTFC
        label={'Add an activity'}
        onChange={onChildChange}
        minLength={0}
        preLoadedOptions={ACTIVITY_OPTIONS_WITH_IDS}
        initialValue={matchedInitialValue}
      />
    ),
    'engagementHistory.activityStatusIds': ({ onChildChange }) => (
      <FormWithFetchAutocompleteByIdTFC
        label={'Add an activity status'}
        onChange={onChildChange}
        minLength={0}
        preLoadedOptions={ACTIVITY_STATUSES_WITH_IDS}
        initialValue={matchedInitialValue}
      />
    ),
    'engagementHistory.subject': ({ onChildChange }) => (
      <TextTFC
        label={'With a subject beginning with (optional)'}
        onChange={onChildChange}
        variant="filled"
        initialValue={simpleInitialValue as string}
      />
    ),
    'engagementHistory.activityDateRange.periodDetails': ({ onChildChange }) => (
      <SelectTFC
        label={'Within time period'}
        onChange={onChildChange as (value: string, fieldMetaData: FieldMetaData) => void}
        isBlankValid={false}
        variant="filled"
        selectMenuItems={[
          { title: '\u00a0', value: '' },
          { title: 'Last 2 days', value: 'last2days' },
          { title: 'Last week', value: 'lastweek' },
          { title: 'Last month', value: 'lastmonth' },
          { title: 'Last 3 months', value: 'last3months' },
          { title: 'Last 6 months', value: 'last6months' },
          { title: 'Last year', value: 'lastyear' },
        ]}
        initialValue={simpleInitialValue as string}
      />
    ),
  };
}
